import { useState, useRef, useCallback, useEffect } from 'react';
import { State } from 'country-state-city';
import { useConnectStore, useEnvStore, useUserStore } from '../../store/models';
import { getCurrency } from '../../utils';
import { getAddOns, calculatePriceSummary, getChargifyTaxRate, getCurrencyLabel, getCurrencySymbol } from '../../conf/chargify';
import useChargifyOtt from './useChargifyOtt';
import useThreeDSecure from './threeDSecure/useThreeDSecure';

const REQUIRED_FIELDS = ['email', 'firstName', 'lastName', 'address', 'country', 'state', 'city', 'zip'];
export const CHECKOUT_CHARGIFY_PANEL_ID = 'checkoutDrawerChargifyPanel';
// finalize3DSecure's underlying API call can be left permanently pending on
// certain backend error responses (see the API layer's returnOnError
// contract). This bounds how long the drawer waits before giving up, so a
// silent backend failure can't strand the user on the 3DS step forever.
const FINALIZE_3DS_TIMEOUT_MS = 20000;

function withTimeout (promise, ms, message) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(message)), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (err) => { clearTimeout(timer); reject(err); },
        );
    });
}

function isScheduleConflictError (error) {
    return ((error && error.message) || '').toLowerCase().includes('already scheduled');
}

function useCheckoutDrawerState ({ open, plan, addOnValues = {}, onClose, onSuccess, subscribeAction, onSubscribed, previewAction, addOnsOnly = false, threeDSecureStore, finalize3DSecureAction, onScheduleConflict }) {
    const connectStore = useConnectStore();
    const envStore = useEnvStore();
    const userStore = useUserStore();

    // Defaults preserve the original Connect new-subscriber behavior for existing
    // callers (PlansBrowseView). Other callers (e.g. the connectPlan upgrade
    // dialog) pass their own subscribeAction/previewAction. Post-subscribe
    // refresh is a caller concern — callers pass onSubscribed to refresh their
    // own state; the drawer itself does nothing by default.
    const doSubscribe = subscribeAction || defaultConnectCreateSubscribe;
    const doOnSubscribed = onSubscribed || (() => Promise.resolve());
    const doPreview = previewAction || defaultConnectPreview;
    const doFinalize3DSecure = finalize3DSecureAction || defaultConnectFinalize3DSecure;

    const submitting = useRef(false);
    // Remembers the token/form used for the subscribe call that triggered 3D
    // secure, so finalize3DSecure() can be replayed once the user authenticates.
    const pending3DSecure = useRef(null);
    // Guards the IP-location prefill (below) to run once per drawer lifetime,
    // so it doesn't clobber fields the user has already edited on reopen.
    const locationPrefilled = useRef(false);

    const [step, setStep] = useState('form'); // 'form' | 'processing' | 'threeDSecure'
    const [form, setForm] = useState({
        email: userStore.publisher?.email || '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        country: 'AU',
        state: '',
        zip: '',
        promo: '',
        promoApplied: false,
        promoLoading: false,
        promoError: '',
        manifest: null,
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [states, setStates] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewTimeoutRef = useRef(null);

    const { formRef, loading: tokenizing, initChargify, generateToken, unload } = useChargifyOtt({
        panelId: CHECKOUT_CHARGIFY_PANEL_ID,
        onToken: (token) => {
            setStep('processing');
            onChargifyTokenSuccess(token);
        },
        onError: (err) => {
            submitting.current = false;
            onChargifyError(err);
        },
    });

    function updateStates (countryCode) {
        const s = State.getStatesOfCountry(countryCode).map((state) => ({
            value: countryCode.toLowerCase() !== 'us' ? state.name : state.isoCode,
            label: state.name,
        }));
        setStates(s);
    }

    useEffect(() => {
        updateStates(form.country);
    }, [form.country]);

    useEffect(() => {
        if (open) {
            setSubmitError('');
            setStep('form');
            pending3DSecure.current = null;
        }
    }, [open]);

    // Prefills country/state/city/zip from the IP-location lookup, mirroring
    // the legacy billing flow's decodeClientInfo().
    useEffect(() => {
        if (!open || locationPrefilled.current) return;
        const ci = userStore.countryInfo;
        if (!ci) return;
        locationPrefilled.current = true;
        setForm(prev => ({
            ...prev,
            country: ci.country_code || prev.country,
            state: ci.state || prev.state,
            city: ci.city || prev.city,
            zip: ci.postal || prev.zip,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Server-computed manifest is the source of truth for tax/subtotal/total —
    // fired on open/plan and re-fired (debounced, like the legacy billing flow's
    // revalidatePreview()) whenever a tax-relevant address field changes. `state`
    // is excluded on purpose: tax is resolved by country only (see
    // getChargifyTaxRate(), whose US per-state branch is dead code), matching
    // revalidatePreview()'s own field list.
    async function runPreview (overrides = {}) {
        setPreviewLoading(true);
        const input = buildSubscriptionInput(overrides);
        const res = await doPreview(input, {
            basePrice: getBasePrice(),
            productHandle: plan.chargifyCode || plan.code,
            productName: plan.name ? `Connect ${plan.name}` : '',
        });
        setPreviewLoading(false);
        if (!res.error) {
            setForm(prev => ({ ...prev, manifest: res.data }));
        }
        return res;
    }

    useEffect(() => {
        if (!open || !plan) return;
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = setTimeout(() => runPreview(), 400);
        return () => clearTimeout(previewTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, plan, form.country, form.zip, form.city, form.promoApplied]);

    // `state` only counts as required when the selected country actually has
    // states to choose from; otherwise the field can never be filled.
    function isFieldRequired (field) {
        if (field === 'state') return states.length > 0;
        return REQUIRED_FIELDS.includes(field);
    }

    function setField (field, value) {
        setForm(prev => {
            const next = { ...prev, [field]: value };
            // Switching country invalidates any previously-selected state.
            if (field === 'country') next.state = '';
            return next;
        });
        setFieldErrors(prev => {
            if (!prev[field]) return prev;
            if (isFieldRequired(field) && !value.trim()) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }

    function validateField (field, value) {
        if (isFieldRequired(field) && !value.trim()) {
            setFieldErrors(prev => ({ ...prev, [field]: 'This field is required.' }));
        } else {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    }

    function handleBlur (field) {
        validateField(field, form[field]);
    }

    function validateAll () {
        const newErrors = {};
        REQUIRED_FIELDS.forEach((field) => {
            if (!isFieldRequired(field)) return;
            if (!form[field] || !form[field].trim()) {
                newErrors[field] = 'This field is required.';
            }
        });
        setFieldErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleApplyPromo () {
        const code = form.promo.trim().toUpperCase();
        if (!code) return;
        setForm(prev => ({ ...prev, promoLoading: true, promoError: '' }));
        const res = await runPreview({ couponCodes: [code] });
        if (res.error) {
            setForm(prev => ({ ...prev, promoLoading: false, promoError: 'Unable to apply promo code.' }));
            return;
        }
        const discount = (res.data && res.data.currentBillingManifest && res.data.currentBillingManifest.totalDiscount) || 0;
        if (discount === 0) {
            setForm(prev => ({ ...prev, promoLoading: false, promoError: 'Invalid promo code.' }));
        } else {
            setForm(prev => ({ ...prev, promoLoading: false, promoApplied: true, promoError: '' }));
        }
    }

    function handleRemovePromo () {
        setForm(prev => ({ ...prev, promo: '', promoApplied: false, promoError: '' }));
    }

    const canPay = REQUIRED_FIELDS.every((f) => !isFieldRequired(f) || (form[f] && form[f].trim()));

    function buildSubscriptionInput (overrides = {}) {
        const ccy = getCurrency(envStore);
        return {
            provider: 'chargify',
            currency: ccy,
            code: plan.chargifyCode || plan.code,
            addOns: getAddOns(addOnValues, { addOns: plan.addOns || [] }),
            country: form.country,
            state: form.state,
            zip: form.zip,
            city: form.city,
            couponCodes: form.promoApplied ? [form.promo.trim().toUpperCase()] : [],
            ...overrides,
        };
    }

    function defaultConnectPreview (input, meta) {
        return connectStore.previewSubscription(input, meta);
    }

    async function defaultConnectCreateSubscribe (token, currentForm) {
        return connectStore.createSubscription({
            code: plan.chargifyCode || plan.code,
            addOns: getAddOns(addOnValues, { addOns: plan.addOns || [] }),
            ott: token,
            firstName: currentForm.firstName,
            lastName: currentForm.lastName,
            country: currentForm.country,
            state: currentForm.state,
            zip: currentForm.zip,
            city: currentForm.city,
            couponCodes: currentForm.promoApplied ? [currentForm.promo.trim().toUpperCase()] : undefined,
        });
    }

    function onPaymentSuccess () {
        onSuccess({ email: form.email, planName: plan.name });
    }

    async function settleSubscribeResult (res) {
        if (res && res.error) {
            if (onScheduleConflict && isScheduleConflictError(res.error)) {
                onScheduleConflict(res.error.message);
                return;
            }
            setSubmitError((res.error && res.error.message) || res.error || 'Subscription failed. Please try again.');
            setStep('form');
            submitting.current = false;
            return;
        }
        // Refresh the current plan/addons so the page reflects the new
        // subscription immediately (e.g. Connect's AlbatoIframeView).
        await doOnSubscribed();
        await onPaymentSuccess();
    }

    async function onChargifyTokenSuccess (token) {
        try {
            const res = await doSubscribe(token, form);
            if (res && res.is3DSecure) {
                // Move to the 'threeDSecure' step — onAuthSuccess/onAuthFailed
                // settle the flow once the publisher completes verification.
                pending3DSecure.current = { token, form };
                setStep('threeDSecure');
                return;
            }
            await settleSubscribeResult(res);
        } catch {
            setSubmitError('Something went wrong activating your subscription. Please try again.');
            setStep('form');
            submitting.current = false;
        }
    }

    function defaultConnectFinalize3DSecure (pending) {
        return connectStore.finalize3DSecure({
            code: plan.chargifyCode || plan.code,
            addOns: getAddOns(addOnValues, { addOns: plan.addOns || [] }),
            ott: pending && pending.token,
            firstName: pending && pending.form.firstName,
            lastName: pending && pending.form.lastName,
        });
    }

    async function onAuthSuccess () {
        const pending = pending3DSecure.current;
        pending3DSecure.current = null;
        if (!pending) return;
        try {
            const res = await withTimeout(
                doFinalize3DSecure(pending),
                FINALIZE_3DS_TIMEOUT_MS,
                'finalize3DSecure timed out',
            );
            await settleSubscribeResult(res);
        } catch {
            setSubmitError('Something went wrong activating your subscription. Please try again.');
            setStep('form');
            submitting.current = false;
        }
    }

    function onAuthFailed (message) {
        pending3DSecure.current = null;
        setSubmitError(message || 'Card authentication failed. Please try again.');
        setStep('form');
        submitting.current = false;
    }

    const { actionURL } = useThreeDSecure({ store: threeDSecureStore || connectStore, onAuthSuccess, onAuthFailed });

    function onChargifyError (err) {
        const msg = (err && err.errors && err.errors[0]) || 'Card details could not be verified. Please check and try again.';
        setSubmitError(msg);
        setStep('form');
    }

    const handleSubmit = useCallback(() => {
        if (submitting.current) return;
        if (!validateAll()) return;
        if (!canPay) return;
        if (!formRef.current) return;
        submitting.current = true;
        setSubmitError('');
        generateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, canPay]);

    function handleCancel () {
        unload();
        onClose();
    }

    function getBasePrice () {
        if (addOnsOnly) return 0;
        if (!plan) return 0;
        return Number((plan.price && plan.price.monthly) || 0);
    }

    // Once a preview manifest has resolved, its tax is the real per-country
    // server figure — derive the displayed rate from it. Until then, fall back
    // to the client-side, currency/country-keyed lookup the legacy billing flow
    // uses so there's no flash of 0%.
    function getTaxRate () {
        const manifest = form.manifest && form.manifest.currentBillingManifest;
        if (manifest && manifest.subTotal > 0) {
            if (addOnsOnly) {
                const { subTotal, taxValue } = getAddOnOnlyTotals(manifest);
                return subTotal > 0 ? Math.round((taxValue / subTotal) * 100) : 0;
            }
            // manifest.subTotal is gross (pre-discount), but manifest.totalTax is
            // computed against each line's taxableAmount (which nets out
            // discounts) — so dividing totalTax by subTotal understates the rate
            // whenever any line carries a discount. Sum taxableAmount instead.
            const totalTaxableAmount = manifest.lineItems.reduce((sum, li) => sum + li.taxableAmount, 0);
            return totalTaxableAmount > 0 ? Math.round((manifest.totalTax / totalTaxableAmount) * 100) : 0;
        }
        if (!plan) return 0;
        return getChargifyTaxRate(plan.price && plan.price.currency, form.country, form.state);
    }

    // Resolves the selected add-ons against their plan definitions using the
    // same helper the existing eCAL add-on-upgrade flow relies on. Only used as
    // the pre-preview fallback — once a manifest resolves (see getSubTotal/getTaxValue/getEffectivePrice),
    // it takes over as the source of truth for totals.
    function getAddOnsSummary () {
        if (!plan) {
            return { addOns: [], totalAddOnsPrice: 0, totalAddOnsRecurringPrice: 0, totalPrice: 0, taxValue: 0, grandTotal: 0, grandTotalRecurring: 0 };
        }
        return calculatePriceSummary(
            { name: plan.name, price: getBasePrice(), addOns: plan.addOns || [] },
            addOnValues,
            getTaxRate(),
            {},
        );
    }

    // Add-on-only totals: the API always previews the full manifest (plan +
    // add-ons together), so for AddOnsDialog we isolate the add-on lines and
    // derive their tax share proportionally from each line's own taxableAmount
    // (not manifest.subTotal, which is gross and would misallocate tax whenever
    // any line carries a discount).
    function getAddOnOnlyTotals (manifest) {
        const lineItems = (manifest && manifest.lineItems) || [];
        const addOnLines = lineItems.filter((li) => li.kind === 'component');
        const subTotal = addOnLines.reduce((sum, li) => sum + li.amount, 0);
        const discountAmount = addOnLines.reduce((sum, li) => sum + li.discountAmount, 0);
        const addOnTaxableAmount = addOnLines.reduce((sum, li) => sum + li.taxableAmount, 0);
        const totalTaxableAmount = lineItems.reduce((sum, li) => sum + li.taxableAmount, 0);
        const taxValue = totalTaxableAmount > 0 ? (manifest.totalTax * addOnTaxableAmount) / totalTaxableAmount : 0;
        return {
            subTotal,
            discountAmount,
            taxValue,
            total: subTotal - discountAmount + taxValue,
        };
    }

    function getSubTotal () {
        if (form.manifest && form.manifest.currentBillingManifest) {
            if (addOnsOnly) return getAddOnOnlyTotals(form.manifest.currentBillingManifest).subTotal;
            return form.manifest.currentBillingManifest.subTotal;
        }
        return getAddOnsSummary().totalPrice;
    }

    function getTaxValue () {
        if (form.manifest && form.manifest.currentBillingManifest) {
            if (addOnsOnly) return getAddOnOnlyTotals(form.manifest.currentBillingManifest).taxValue;
            return form.manifest.currentBillingManifest.totalTax;
        }
        return getAddOnsSummary().taxValue;
    }

    // The recurring-only total (excludes one-off add-ons), tax-inclusive —
    // what the subscription bills going forward after today's charge. The
    // manifest's nextBillingManifest already excludes one-off add-ons.
    function getTotalRecurring () {
        if (form.manifest && form.manifest.nextBillingManifest) {
            if (addOnsOnly) return getAddOnOnlyTotals(form.manifest.nextBillingManifest).total;
            return form.manifest.nextBillingManifest.total;
        }
        return getAddOnsSummary().grandTotalRecurring;
    }

    function getEffectivePrice () {
        if (form.manifest && form.manifest.currentBillingManifest) {
            if (addOnsOnly) return getAddOnOnlyTotals(form.manifest.currentBillingManifest).total;
            return form.manifest.currentBillingManifest.total;
        }
        return getAddOnsSummary().grandTotal;
    }

    function getDiscountAmount () {
        if (form.manifest && form.manifest.currentBillingManifest) {
            if (addOnsOnly) return getAddOnOnlyTotals(form.manifest.currentBillingManifest).discountAmount;
            return form.manifest.currentBillingManifest.totalDiscount;
        }
        return 0;
    }

    function formatPrice (amount) {
        if (!plan) return '';
        return `${getCurrencySymbol(plan.price && plan.price.currency)}${Number(amount).toLocaleString()}`;
    }

    // Currency-code-suffixed formatting (e.g. "240.30 AUD") for the Plan
    // Summary card, matching the legacy billing flow's price display.
    function formatPriceUnit (amount) {
        if (!plan) return '';
        return `${Number(amount).toFixed(2)} ${getCurrencyLabel(plan.price && plan.price.currency)}`;
    }

    return {
        formRef,
        step,
        tokenizing,
        form,
        getDiscountAmount,
        previewLoading,
        states,
        fieldErrors,
        submitError,
        canPay,
        initChargify,
        setField,
        handleBlur,
        handleApplyPromo,
        handleRemovePromo,
        handleSubmit,
        handleCancel,
        getBasePrice,
        getEffectivePrice,
        getAddOnsSummary,
        getTaxRate,
        getSubTotal,
        getTaxValue,
        getTotalRecurring,
        formatPrice,
        formatPriceUnit,
        actionURL,
    };
}

export default useCheckoutDrawerState;
